import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { FormButtons } from '../../../../../../../../UI/Form/FormButtons';
import {
  FolderOpen as FolderIcon,
  ContentCopy as CopyIcon,
  KeyboardArrowUp,
  KeyboardArrowDown,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { handlebarsToJsonSchemaV2 } from '../handlebarsToJsonSchema';
import { NgTooltip } from '../../../../../../../../UI';
import { FolderManagerMoveDialog } from '../../../../../../../../FolderManager/Components/FolderManagerMoveDialog';
import { useGetUserRootFoldersQuery, useGetFolderPathQuery } from '../../../../../../../../../services';

// Styled Components
const Container = styled.div`
  padding: 24px;
  background: #ffffff;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Header = styled.div`
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
`;

const FieldCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
  }
`;

const FieldHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const FieldNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldName = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #1a1a1a;
  background: #ffffff;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
`;

const OrderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #007bff;
    background: #f0f7ff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    font-size: 18px;
    color: #495057;
  }
`;

const FieldContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  min-width: 120px;
`;

const StyledSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  min-width: 150px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const StyledInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6c757d;
  font-size: 16px;
`;

const FolderPickerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  min-width: 200px;
  transition: all 0.2s ease;
  text-align: left;
  justify-content: flex-start;

  &:hover {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
    border-color: #e9ecef;
  }

  .folder-icon {
    color: #6c757d;
    font-size: 18px;
  }

  .folder-path {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .placeholder {
    color: #6c757d;
    font-style: italic;
  }
`;

const FilterInfo = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
  font-style: italic;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: #007bff;
  color: white;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 8px;

  &:hover {
    background: #0056b3;
    border-color: #0056b3;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #6c757d;
    border-color: #6c757d;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .copy-icon {
    font-size: 14px;
  }
`;

const QueryKeyContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const RowContainer = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

const RangeInputsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const RangeInput = styled(StyledInput)`
  min-width: 80px;
  max-width: 100px;
`;

const RangeLabel = styled.span`
  font-size: 12px;
  color: #6c757d;
  white-space: nowrap;
`;

// Types

interface Props {
  entrypoint: { name: string; html: string; displayName?: string; instanceId?: string };
  blocks: { name: string; html: string | null }[];
  onSave: (schema: any, displayName?: string) => Promise<void> | void;
  schema: any;
  labels?: {
    title?: string;
    subtitle?: string;
    configurationSectionTitle?: string;
  };
  folderPickerZIndex?: number;
  fieldWarnings?: Record<string, string>;
}

interface FormValues {
  blockDisplayName: string;
  fields: Record<string, any>;
}

// Helpers

const sortedFieldNames = (schema: any): string[] =>
  Object.entries(schema?.properties || {})
    .sort(([, a]: any, [, b]: any) => (a.inputOptions?.order ?? 0) - (b.inputOptions?.order ?? 0))
    .map(([name]) => name);

const schemaToDefaultValues = (schema: any, entrypoint: Props['entrypoint']): FormValues => ({
  blockDisplayName:
    schema?.displayName || schema?.title || entrypoint.displayName || entrypoint.name || '',
  fields: Object.fromEntries(
    Object.entries(schema?.properties || {}).map(([name, value]: any) => [
      name,
      { ...value, displayName: value.displayName ?? '' },
    ]),
  ),
});

const formValuesToSchema = (data: FormValues, sourceSchema: any, fieldOrder: string[]): any => {
  const properties: Record<string, any> = {};

  fieldOrder.forEach((name, index) => {
    const fieldData = data.fields[name] || {};
    const inputOptions: Record<string, any> = { ...(fieldData.inputOptions || {}), order: index };

    // Remove undefined inputOptions keys
    Object.keys(inputOptions).forEach((key) => {
      if (inputOptions[key] === undefined) delete inputOptions[key];
    });

    const fieldResult: any = {
      ...fieldData,
      displayName: (fieldData.displayName || '').trim(),
      inputOptions,
    };

    // Convert minItems/maxItems: keep as number or remove
    if (fieldResult.minItems !== undefined && fieldResult.minItems !== '' && !isNaN(Number(fieldResult.minItems))) {
      fieldResult.minItems = Number(fieldResult.minItems);
    } else {
      delete fieldResult.minItems;
    }
    if (fieldResult.maxItems !== undefined && fieldResult.maxItems !== '' && !isNaN(Number(fieldResult.maxItems))) {
      fieldResult.maxItems = Number(fieldResult.maxItems);
    } else {
      delete fieldResult.maxItems;
    }

    properties[name] = fieldResult;
  });

  return { ...sourceSchema, properties };
};

/**
 * Filter blocks that match the expected schema structure
 */
const filterBlocksBySchema = (blocks: any[], expectedSchema: any): any[] => {
  if (!expectedSchema?.properties) {
    return blocks;
  }

  const expectedFields = Object.keys(expectedSchema.properties);

  return blocks.filter((block) => {
    const blockName = block.name;
    if (!block.html) return false;

    try {
      const blockSchema = handlebarsToJsonSchemaV2(blockName, [{ name: blockName, partial: block.html }]);
      if (!blockSchema?.properties) return false;

      const blockFields = Object.keys(blockSchema.properties);
      const matchingFields = expectedFields.filter((field) =>
        blockFields.some(
          (bField) =>
            bField === field && blockSchema.properties[bField].type === expectedSchema.properties[field].type,
        ),
      );

      return matchingFields.length > 0;
    } catch (error) {
      console.warn(`Error parsing schema for block ${blockName}:`, error);
      return false;
    }
  });
};

// Sub-components — all read/write form state via useFormContext

const ImagePickerInputSettings = ({
  fieldName,
  folderPickerZIndex = 99,
}: {
  fieldName: string;
  folderPickerZIndex?: number;
}) => {
  const variantOptions: ImagePickerDynamicFormField['inputOptions']['pickerVariant'][] = ['default', 'carousel'];
  const prefix = `fields.${fieldName}.inputOptions`;

  const { control, setValue, getValues, watch } = useFormContext<FormValues>();
  const sourcePath = (watch(`${prefix}.sourcePath` as any) as string) || '';

  const [moveDialogState, setMoveDialogState] = useState({
    opened: false,
    rootFolder: null as any,
    selectedItems: [] as any[],
  });

  const { data: userRootFolders } = useGetUserRootFoldersQuery();
  const rootFolder = userRootFolders?.find((f) => f.slug === 'company-assets');

  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(() => {
    const path = (getValues(`${prefix}.sourcePath` as any) as string) || '';
    if (path.includes('crn:folders:')) {
      const id = Number(path.split(':').pop());
      return isNaN(id) ? null : id;
    }
    return null;
  });

  const { data: folderBreadcrumbs } = useGetFolderPathQuery(
    { folderId: selectedFolderId! },
    { skip: !selectedFolderId },
  );

  const folderPath = useMemo(() => {
    if (!folderBreadcrumbs?.length) return sourcePath;
    return folderBreadcrumbs.map((item) => item?.name).join('/');
  }, [folderBreadcrumbs, sourcePath]);

  const handleFolderSelectClick = () => {
    if (!rootFolder) {
      console.warn('Could not open folder selector: no root folder available!');
      return;
    }
    setMoveDialogState({ opened: true, rootFolder: rootFolder as any, selectedItems: [] });
  };

  const handlePickDestinationFolder = (data: any) => {
    if (!data) {
      setMoveDialogState({ opened: false, rootFolder: null, selectedItems: [] });
      return;
    }
    const targetFolderId = data.destinationId as number;
    setSelectedFolderId(targetFolderId);
    setValue(`${prefix}.sourcePath` as any, `crn:folders:${targetFolderId}`, { shouldDirty: true });
    setMoveDialogState({ opened: false, rootFolder: null, selectedItems: [] });
  };

  return (
    <>
      <OptionsContainer>
        <RowContainer>
          <Label>Source Folder:</Label>
          <FolderPickerButton type="button" onClick={handleFolderSelectClick} disabled={!rootFolder}>
            <FolderIcon className="folder-icon" />
            <span className={folderPath ? 'folder-path' : 'placeholder'}>
              {folderPath || 'Select a folder...'}
            </span>
          </FolderPickerButton>
        </RowContainer>
        <RowContainer>
          <Label>Variant:</Label>
          <Controller
            name={`${prefix}.pickerVariant` as any}
            control={control}
            render={({ field }) => (
              <StyledSelect value={field.value || 'default'} onChange={(e) => field.onChange(e.target.value)}>
                {variantOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </StyledSelect>
            )}
          />
        </RowContainer>
      </OptionsContainer>

      {moveDialogState?.opened && (
        <FolderManagerMoveDialog
          dialogState={moveDialogState as any}
          submitLabel="Select Folder"
          onDismiss={handlePickDestinationFolder}
          includeUserRoot={false}
          targetOnly
          zIndex={folderPickerZIndex}
        />
      )}
    </>
  );
};

const QueryKeyInputSettings = ({ fieldName }: { fieldName: string }) => {
  const { control } = useFormContext<FormValues>();
  const prefix = `fields.${fieldName}.inputOptions`;

  return (
    <RowContainer>
      <Label>GQL Query Key:</Label>
      <Controller
        name={`${prefix}.gqlQueryKey` as any}
        control={control}
        render={({ field }) => (
          <StyledInput
            type="text"
            placeholder="e.g. resources"
            value={field.value || ''}
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      />
    </RowContainer>
  );
};

const OrderedListInputSettings = ({
  fieldName,
  blocks,
}: {
  fieldName: string;
  blocks: any[];
}) => {
  const { control, watch } = useFormContext<FormValues>();
  const prefix = `fields.${fieldName}`;
  const fieldValue = watch(prefix as any) as any;
  const [copySuccess, setCopySuccess] = useState(false);

  const filteredBlocks = useMemo(() => {
    const itemsSchema = fieldValue?.items;
    if (!itemsSchema) return blocks;
    return filterBlocksBySchema(blocks, itemsSchema);
  }, [blocks, fieldValue?.items, fieldName]);

  const generateGraphQLTemplate = () => {
    const alias = fieldValue?.inputOptions?.gqlQueryKey || 'myAlias';
    const itemsSchema = fieldValue?.items;
    if (!itemsSchema?.properties) return `${alias}: REPLACE_ME {\n  ID\n}`;
    const keys = Object.keys(itemsSchema.properties);
    const formattedKeys = keys.map((key) => `    ${key} : REPLACE_ME`).join('\n');
    return `${alias}: REPLACE_ME {\n    ID\n${formattedKeys}\n  }`;
  };

  const handleCopyTemplate = async () => {
    const template = generateGraphQLTemplate();
    try {
      await navigator.clipboard.writeText(template);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = template;
      document.body.appendChild(textArea);
      textArea.select();
      // eslint-disable-next-line deprecation/deprecation
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  return (
    <OptionsContainer>
      <RowContainer>
        <Label>GQL Query Key:</Label>
        <QueryKeyContainer>
          <Controller
            name={`${prefix}.inputOptions.gqlQueryKey` as any}
            control={control}
            render={({ field }) => (
              <StyledInput
                type="text"
                placeholder="override gql query key"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          <CopyButton
            type="button"
            onClick={handleCopyTemplate}
            disabled={!fieldValue?.inputOptions?.gqlQueryKey}
            title="Copy GraphQL query template"
            style={{
              background: copySuccess ? '#28a745' : undefined,
              borderColor: copySuccess ? '#28a745' : undefined,
            }}
          >
            <CopyIcon className="copy-icon" />
            {copySuccess ? 'Copied!' : 'Copy Query'}
          </CopyButton>
        </QueryKeyContainer>
      </RowContainer>

      <RowContainer>
        <Label>Preview Component:</Label>
        <div>
          <Controller
            name={`${prefix}.inputOptions.previewComponent` as any}
            control={control}
            render={({ field }) => (
              <StyledSelect value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)}>
                <option value="">None</option>
                {filteredBlocks.map((block) => (
                  <option key={block.name} value={block.name}>
                    {block.name}
                  </option>
                ))}
              </StyledSelect>
            )}
          />
          {filteredBlocks.length !== blocks.length && (
            <FilterInfo>
              Showing {filteredBlocks.length} of {blocks.length} blocks (filtered by schema compatibility)
            </FilterInfo>
          )}
        </div>
      </RowContainer>

      <RowContainer>
        <Label>Item Range:</Label>
        <RangeInputsContainer>
          <div>
            <RangeLabel>Min:</RangeLabel>
            <Controller
              name={`${prefix}.minItems` as any}
              control={control}
              render={({ field }) => (
                <RangeInput
                  type="number"
                  placeholder="0"
                  min="0"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? undefined : parseInt(e.target.value, 10))
                  }
                />
              )}
            />
          </div>
          <div>
            <RangeLabel>Max:</RangeLabel>
            <Controller
              name={`${prefix}.maxItems` as any}
              control={control}
              render={({ field }) => (
                <RangeInput
                  type="number"
                  placeholder="∞"
                  min="1"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? undefined : parseInt(e.target.value, 10))
                  }
                />
              )}
            />
          </div>
        </RangeInputsContainer>
      </RowContainer>

      <RowContainer>
        <Label>Filter Type:</Label>
        <Controller
          name={`${prefix}.inputOptions.filterType` as any}
          control={control}
          render={({ field }) => (
            <StyledSelect value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
              <option value="">None</option>
              <option value="text">Text</option>
              <option value="attributes">Attributes</option>
            </StyledSelect>
          )}
        />
      </RowContainer>
    </OptionsContainer>
  );
};

const InputSettings = ({
  fieldName,
  blocks,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  folderPickerZIndex,
  warning,
}: {
  fieldName: string;
  blocks: any[];
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  folderPickerZIndex?: number;
  warning?: string;
}) => {
  const { control, setValue, watch, getValues } = useFormContext<FormValues>();
  const prefix = `fields.${fieldName}`;
  const inputType = watch(`${prefix}.inputType` as any) as string;

  const inputTypeOptions: DynamicFormField['inputType'][] = useMemo(() => {
    switch (inputType) {
      case 'ordered-list':
      case 'queryKey':
        return ['ordered-list', 'queryKey'];
      default:
        return [
          'email',
          'hidden',
          'image-picker',
          'number',
          'select',
          'text',
          'textarea',
          'video-picker',
          'rich-text',
          'checkbox',
        ];
    }
  }, [inputType]);

  const handleInputTypeChange = (newType: string) => {
    const opts = { shouldDirty: true };
    switch (newType) {
      case 'image-picker':
      case 'video-picker':
        setValue(`${prefix}.inputType` as any, newType, opts);
        setValue(`${prefix}.type` as any, 'number', opts);
        setValue(`${prefix}.inputOptions` as any, { pickerVariant: 'default', sourcePath: '' }, opts);
        break;
      case 'ordered-list':
        setValue(`${prefix}.inputType` as any, 'ordered-list', opts);
        setValue(`${prefix}.type` as any, 'array', opts);
        setValue(`${prefix}.inputOptions` as any, { variant: 'default' }, opts);
        break;
      case 'queryKey':
        setValue(`${prefix}.inputType` as any, 'queryKey', opts);
        setValue(`${prefix}.type` as any, 'array', opts);
        setValue(
          `${prefix}.inputOptions` as any,
          { gqlQueryKey: getValues(`${prefix}.inputOptions.gqlQueryKey` as any) || '' },
          opts,
        );
        break;
      case 'checkbox':
        setValue(`${prefix}.inputType` as any, 'checkbox', opts);
        setValue(`${prefix}.type` as any, 'boolean', opts);
        setValue(`${prefix}.inputOptions` as any, {}, opts);
        break;
      default:
        setValue(`${prefix}.inputType` as any, newType, opts);
        setValue(`${prefix}.type` as any, 'string', opts);
        setValue(`${prefix}.inputOptions` as any, {}, opts);
        break;
    }
  };

  return (
    <FieldCard>
      <FieldHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FieldName>{fieldName}</FieldName>
          {warning && (
            <NgTooltip title={warning} placement="top">
              <span style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
                <WarningAmberIcon sx={{ color: '#ffc107', fontSize: 18 }} />
              </span>
            </NgTooltip>
          )}
        </div>
        <FieldNameContainer>
          <Controller
            name={`${prefix}.inputType` as any}
            control={control}
            render={({ field }) => (
              <StyledSelect value={field.value} onChange={(e) => handleInputTypeChange(e.target.value)}>
                {inputTypeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </StyledSelect>
            )}
          />
          <OrderButton type="button" onClick={onMoveUp} disabled={isFirst} title="Move up">
            <KeyboardArrowUp />
          </OrderButton>
          <OrderButton type="button" onClick={onMoveDown} disabled={isLast} title="Move down">
            <KeyboardArrowDown />
          </OrderButton>
        </FieldNameContainer>
      </FieldHeader>

      <FieldContent>
        <RowContainer>
          <Label>Display Name:</Label>
          <Controller
            name={`${prefix}.displayName` as any}
            control={control}
            render={({ field }) => (
              <StyledInput
                type="text"
                placeholder={`Display name for "${fieldName}"`}
                value={field.value || ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
        </RowContainer>

        {(inputType === 'image-picker' || inputType === 'video-picker') && (
          <ImagePickerInputSettings fieldName={fieldName} folderPickerZIndex={folderPickerZIndex} />
        )}
        {inputType === 'ordered-list' && (
          <OrderedListInputSettings fieldName={fieldName} blocks={blocks} />
        )}
        {inputType === 'queryKey' && <QueryKeyInputSettings fieldName={fieldName} />}
      </FieldContent>
    </FieldCard>
  );
};

export const BlockInputsSettings: FC<Props> = ({
  entrypoint,
  blocks,
  onSave,
  labels,
  folderPickerZIndex,
  fieldWarnings,
  ...props
}) => {
  const [sourceSchema, setSourceSchema] = useState<any>(null);
  const [fieldOrder, setFieldOrder] = useState<string[]>([]);
  const [isOrderDirty, setIsOrderDirty] = useState(false);

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { blockDisplayName: '', fields: {} },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = methods;

  useEffect(() => {
    const schema = props.schema
      ? props.schema
      : (() => {
          const s = handlebarsToJsonSchemaV2(
            `${entrypoint.name}__wrapper`,
            blocks
              .map((b) => ({ name: b.name, partial: b.html || '' }))
              .concat([
                { name: entrypoint.name, partial: entrypoint.html },
                { name: `${entrypoint.name}__wrapper`, partial: `{{> ${entrypoint.name} }}` },
              ]),
          );
          return s?.properties[entrypoint.name];
        })();

    setSourceSchema(schema);
    reset(schemaToDefaultValues(schema, entrypoint));
    setFieldOrder(sortedFieldNames(schema));
    setIsOrderDirty(false);
  }, [props.schema]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveField = (fieldName: string, direction: 'up' | 'down') => {
    setFieldOrder((prev) => {
      const index = prev.indexOf(fieldName);
      if (index === -1) return prev;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      setIsOrderDirty(true);
      return next;
    });
  };

  const handleReset = () => {
    reset();
    setFieldOrder(sortedFieldNames(sourceSchema));
    setIsOrderDirty(false);
  };

  const onSubmit = handleSubmit(async (data) => {
    const schema = formValuesToSchema(data, sourceSchema, fieldOrder);
    await onSave(schema, data.blockDisplayName.trim());
    reset(data);
    setIsOrderDirty(false);
  });

  const hasFields = sourceSchema?.properties && Object.keys(sourceSchema.properties).length > 0;

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <Container>
          <Header>
            <Title>{labels?.title ?? 'Block Input Settings'}</Title>
            <Subtitle>
              {labels?.subtitle ?? `Configure input types and options for "${entrypoint.name}" block`}
            </Subtitle>
          </Header>

          <FieldCard style={{ marginBottom: '24px' }}>
            <FieldHeader>
              <FieldName>{labels?.configurationSectionTitle ?? 'Block Configuration'}</FieldName>
            </FieldHeader>
            <FieldContent>
              <RowContainer>
                <Label>Display Name:</Label>
                <Controller
                  name="blockDisplayName"
                  control={control}
                  render={({ field }) => (
                    <StyledInput
                      type="text"
                      placeholder="Enter display name for this block instance"
                      value={field.value || ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </RowContainer>
            </FieldContent>
          </FieldCard>

          {hasFields ? (
            <FieldsContainer>
              {fieldOrder.map((fieldName, index) => (
                <InputSettings
                  key={fieldName}
                  fieldName={fieldName}
                  blocks={blocks}
                  onMoveUp={() => moveField(fieldName, 'up')}
                  onMoveDown={() => moveField(fieldName, 'down')}
                  isFirst={index === 0}
                  isLast={index === fieldOrder.length - 1}
                  folderPickerZIndex={folderPickerZIndex}
                  warning={fieldWarnings?.[fieldName]}
                />
              ))}
            </FieldsContainer>
          ) : (
            <EmptyState>
              No input fields found for this block.
              <br />
              Make sure the block template contains Handlebars expressions.
            </EmptyState>
          )}

          <FormButtons
            isLoading={isSubmitting}
            isDirty={isDirty || isOrderDirty}
            isValid
            reset={handleReset}
            submitLabel="Submit"
            placement="right"
          />
        </Container>
      </form>
    </FormProvider>
  );
};
